<script setup>
import ActionButton from '@/components/widgets/barActions/ActionButton.vue';
import { useDisplay } from 'vuetify';

const { mdAndDown } = useDisplay();
const emit = defineEmits(['action']);

defineProps({
    actions: {
        type: Array,
        required: true,
        default: () => []
    },
    rightActions: {
        type: Array,
        default: () => []
    },
    showToggleSidebarButton: {
        type: Boolean,
        default: false
    },
    mode: {
        type: String
    },
    hideRemoveAction: {
        type: Boolean,
        default: false
    }
});
</script>

<template>
    <div class="bar-action py-1 d-flex align-center">
        <template v-for="action in actions" :key="action.title">
            <!-- Show menu for processAlert action -->
            <v-menu
                v-if="
                    action.type === 'select' &&
                    !action.hide
                "
            >
                <template v-slot:activator="{ props }">
                    <ActionButton
                        v-bind="props"
                        :icon="action.icon"
                        :title="action.title"
                        :hidden="action.hide"
                        :disabled="mode === 'edit'"
                        @click="
                            (event) => props.onClick && props.onClick(event)
                        "
                    />
                </template>

                <v-list>
                    <v-list-item
                        v-for="item in action.options"
                        :key="item.value"
                        :value="item.value"
                        :prepend-icon="item.icon"
                        @click="
                            emit('action', {
                                name: action.name,
                                value: item.value
                            })
                        "
                    >
                        <v-list-item-title>{{
                            $t(item.title)
                        }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>

            <!-- Regular ActionButton for other actions -->
            <ActionButton
                v-if="action.type !== 'select'"
                :title="!mdAndDown ? action.title : undefined"
                :icon="action.icon"
                :hidden="action.hide"
                @click="emit('action', action.name)"
                :disabled="mode === 'edit' || action.disabledOnView"
            />
        </template>
        <v-spacer />
        <ActionButton
            v-for="action in rightActions"
            :key="action.title"
            :title="!mdAndDown ? action.title : undefined"
            :icon="action.icon"
            :hidden="action.hide"
            @click="emit('action', action.name)"
            :disabled="mode === 'edit'"
        />
        <ActionButton
            v-if="showToggleSidebarButton"
            :title="undefined"
            icon="mdi-arrow-expand-horizontal"
            :hidden="false"
            @click="emit('action', 'toggleSidebar')"
        />
    </div>
</template>

<style lang="scss" scoped>
.bar-action {
    border-top: 1px solid #e7e9eb;
    border-bottom: 1px solid #e7e9eb;
}

.small-select {
    min-width: 120px;
    max-width: 140px;

    // Target the input wrapper
    :deep(.v-input) {
        font-size: 12px !important;
    }

    // Target the field container
    :deep(.v-field) {
        min-height: 30px !important;
        font-size: 12px !important;
    }

    // Target the actual input area
    :deep(.v-field__input) {
        min-height: 28px !important;
        padding-top: 2px !important;
        padding-bottom: 2px !important;
        font-size: 12px !important;
        line-height: 1.2 !important;
    }

    // Target the field outline
    :deep(.v-field__outline) {
        --v-field-border-width: 1px;
    }

    // Target the label
    :deep(.v-label) {
        opacity: 1 !important;
        font-size: 12px !important;
        font-weight: bold;
        top: 50% !important;
        transform: translateY(-50%) !important;
    }

    // Target the label when focused/filled
    :deep(.v-label--active) {
        font-size: 10px !important;
        transform: translateY(-18px) !important;
    }

    // Target the append inner (dropdown arrow)
    :deep(.v-field__append-inner) {
        padding-top: 2px !important;

        .v-icon {
            font-size: 16px !important;
        }
    }
}
</style>
